import ApiClient from './ApiClient'
import { withQuery } from '../../utils/urlParams'

const issuesBase = collectId => `/collects/${collectId}/issues`
const reportingBase = collectId => `/collects/${collectId}/reporting`
const operatorOptionsUrl = collectId => `${reportingBase(collectId)}/operator_options.json`

const REMOVED_PHOTO_IDS_KEY = 'removed_photo_ids'
const USER_IDS_KEY = 'user_ids'
const ISSUE_STATUS_ID_KEY = 'issue_status_id'

const hasPhotoChanges = payload =>
  payload.photos?.length > 0 || payload[REMOVED_PHOTO_IDS_KEY]?.length > 0

// Photos require multipart; ActiveStorage reads issue[photos][] from the form.
const buildIssueFormData = payload => {
  const form = new FormData()
  if (payload.status)
    form.append('issue[status]', payload.status)
  if (payload[ISSUE_STATUS_ID_KEY])
    form.append('issue[issue_status_id]', payload[ISSUE_STATUS_ID_KEY])
  form.append('issue[comment]', payload.comment ?? '')
  const userIds = payload[USER_IDS_KEY] || []
  if (userIds.length === 0)
    form.append('issue[user_ids][]', '')
  userIds.forEach(id => form.append('issue[user_ids][]', id))
  const photos = payload.photos || []
  photos.forEach(file => form.append('issue[photos][]', file))
  const removedPhotoIds = payload[REMOVED_PHOTO_IDS_KEY] || []
  removedPhotoIds.forEach(id => form.append('issue[removed_photo_ids][]', id))
  return form
}

const listParams = options => {
  const {
    page, items, search, issueStatus, issueContent, sort, direction, draftId, columns, variables,
    startDate, endDate
  } = options
  return [
    ['page', page],
    ['limit', items],
    ['q', search],
    ['issue_status', issueStatus],
    ['issue_content', issueContent],
    ['sort', sort],
    ['direction', direction],
    ['draft_id', draftId],
    ['start_date', startDate],
    ['end_date', endDate],
    /*
     * Narrow extra_values server-side. `none` (sentinel) tells the API to skip
     * serializing all displayed_question_ids when no extra column is shown.
     */
    ['columns', columns && columns.length > 0 ? columns.join(',') : 'none'],
    ['variables', variables && variables.length > 0 ? variables.join(',') : 'none']
  ]
}

const isPresent = value => value !== null && typeof value !== 'undefined' && value !== ''

const batchFilterEntries = ({ search, issueStatus, issueContent, startDate, endDate }) => [
  ['q', search],
  ['issue_status', issueStatus],
  ['issue_content', issueContent],
  ['start_date', startDate],
  ['end_date', endDate]
]

const batchUpdateBody = options => {
  const { issueStatusId, issueIds, allFiltered, draftId } = options
  const entries = [
    ['issue_status_id', issueStatusId],
    ['draft_id', draftId],
    ...allFiltered ? [['all_filtered', true], ...batchFilterEntries(options)] : [['issue_ids', issueIds]]
  ]
  return Object.fromEntries(entries.filter(([, value]) => isPresent(value)))
}

class IssuesService {
  static getIssues(collectId, params = {}) {
    return ApiClient.get(withQuery(`${issuesBase(collectId)}.json`, listParams(params)))
  }

  static getIssue(collectId, issueId) {
    return ApiClient.get(`${issuesBase(collectId)}/${issueId}.json`)
  }

  static updateIssue(collectId, issueId, payload) {
    const url = `${issuesBase(collectId)}/${issueId}.json`
    if (hasPhotoChanges(payload))
      return ApiClient.patchFormData(url, buildIssueFormData(payload))

    return ApiClient.request(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ issue: payload })
    })
  }

  static batchUpdate(collectId, options) {
    return ApiClient.patch(`${issuesBase(collectId)}/batch_update.json`, batchUpdateBody(options))
  }

  static shareIssue(collectId, issueId, payload) {
    return ApiClient.post(`${issuesBase(collectId)}/${issueId}/share.json`, { issue: payload })
  }

  static getAssignableUsers(collectId) {
    return ApiClient.get(operatorOptionsUrl(collectId))
  }
}

export default IssuesService
